import React, { useState } from "react";
import { Category, TransactionType } from "../types";
import { NewTransaction } from "../useFinance";
import { parseAmount, toDateKey } from "../format";

interface TransactionFormProps {
    categories: Category[];
    onAdd: (transaction: NewTransaction) => void;
}

export function TransactionForm({ categories, onAdd }: TransactionFormProps) {
    const [type, setType] = useState<TransactionType>("expense");
    const [amount, setAmount] = useState("");
    const [categoryId, setCategoryId] = useState("");
    const [date, setDate] = useState(() => toDateKey(new Date()));
    const [note, setNote] = useState("");
    const [error, setError] = useState("");
    const [saved, setSaved] = useState(false);

    const options = categories.filter(c => c.type === type);
    const selectedCategory = options.some(c => c.id === categoryId) ? categoryId : options[0]?.id || "";

    const submit = (e: React.FormEvent) => {
        e.preventDefault();
        const cents = parseAmount(amount);
        if (cents === null) {
            setError("Enter an amount greater than 0.");
            return;
        }
        if (!date) {
            setError("Pick a date.");
            return;
        }
        onAdd({ type, amount: cents, categoryId: selectedCategory, date, note: note.trim() });
        setAmount("");
        setNote("");
        setError("");
        setSaved(true);
        window.setTimeout(() => setSaved(false), 1800);
    };

    return (
        <section className="fin-card">
            <div className="fin-card-head">
                <h3>Add transaction</h3>
            </div>
            <form className="fin-form" onSubmit={submit} noValidate>
                <div className="fin-type-switch" role="radiogroup" aria-label="Transaction type">
                    {(["expense", "income"] as TransactionType[]).map(t => (
                        <button
                            key={t}
                            type="button"
                            role="radio"
                            aria-checked={type === t}
                            className={type === t ? `active ${t}` : ""}
                            onClick={() => setType(t)}
                        >
                            {t === "expense" ? "Expense" : "Income"}
                        </button>
                    ))}
                </div>
                <label>
                    Amount (€)
                    <input inputMode="decimal" placeholder="0.00" value={amount} onChange={e => setAmount(e.target.value)} />
                </label>
                <label>
                    Category
                    <select value={selectedCategory} onChange={e => setCategoryId(e.target.value)}>
                        {options.map(c => (
                            <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
                        ))}
                    </select>
                </label>
                <label>
                    Date
                    <input type="date" value={date} onChange={e => setDate(e.target.value)} />
                </label>
                <label>
                    Note <small>(optional)</small>
                    <input placeholder="e.g. Weekly groceries" value={note} maxLength={80} onChange={e => setNote(e.target.value)} />
                </label>
                {error && <p className="fin-error" role="alert">{error}</p>}
                <button type="submit" className="fin-primary">{saved ? "✓ Added" : "Add transaction"}</button>
            </form>
        </section>
    );
}
